import React, { useEffect, useState } from "react";
import { Alert, Col, Row, Space, Typography } from "antd";
import DisplayNextOpenings from "../DIsplayNextOpenings";
import ServiceConsultation from "../SpecialServiceMessage";

import TimeButton from "@/app/components/TimeButton";
import { EditionJson } from "@/lib/api";
import { UserOpeningsData, ServiceOption, Service } from "@/lib/api/types";
import OpeningLoading from "@/app/components/OpeningLoading";
import { AppointmentForm } from "@/lib/hooks/useAppointment";

interface StylistOpeningsProps {
  userId: string;
  userPathId: string;
  userName: string;
  date: string;
  refreshCount: number;
  services: (Service & {
    option?: ServiceOption;
  })[];
  isTodayOnVacation?: boolean;
  onTimeClick: (args: AppointmentForm) => void;
  onRefresh?: () => void;
}

export const StylistOpenings = ({
  userId,
  userPathId,
  userName,
  date,
  services,
  refreshCount,
  isTodayOnVacation,
  onTimeClick,
  onRefresh,
}: StylistOpeningsProps) => {
  const [isLoading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState<UserOpeningsData[]>([]);

  let openingCount = 0;
  let duration = 0;
  const servicesRequiredConsultation: Service[] = [];
  services.forEach((service) => {
    if (service.hasConsultation) {
      servicesRequiredConsultation.push(service);
    }
    duration += service.duration;
  });

  const handleTimeClick = React.useCallback(
    (schedule: UserOpeningsData["schedule"], time: string) => {
      onTimeClick({
        userId,
        stylistName: userName,
        locationName: schedule.locationName,
        scheduleId: schedule._id,
        services,
        time,
        date,
      });
    },
    [userId, userName, services, date, onTimeClick]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const _res = await EditionJson.getOpeningsByDuration(
          userPathId,
          date,
          duration
        );
        setSchedules(_res);
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    };

    if (services.length > 0) load(); // ✅ Only run if services exist
  }, [userPathId, date, refreshCount, duration]);

  if (servicesRequiredConsultation.length > 0)
    return (
      <ServiceConsultation
        userId={userId}
        userName={userName}
        services={servicesRequiredConsultation}
      />
    );

  if (isLoading) return <OpeningLoading />;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {isTodayOnVacation ? (
        <Alert type="success" message="On vacation" />
      ) : (
        <>
          <Typography.Text strong>Choose a time</Typography.Text>
          {schedules.map((item, i) => {
            const { schedule, openings } = item;
            if (!openings || openings.length === 0) {
              return null;
            } else {
              openingCount++;
              return (
                <Space direction="vertical" style={{ width: "100%" }} key={i}>
                  {schedules.length > 1 && (
                    <Typography.Text strong>
                      {schedule.locationName}
                    </Typography.Text>
                  )}
                  <Row gutter={[8, 8]}>
                    {openings.map((time: string, i: number) => {
                      return (
                        <Col key={`${time}-${i}`} span={8}>
                          <TimeButton
                            onClick={() => handleTimeClick(schedule, time)}
                            time={time}
                            isSelected={false}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                </Space>
              );
            }
          })}
        </>
      )}
      {openingCount === 0 && (
        <DisplayNextOpenings
          userId={userId}
          userName={userName}
          userPathName={userPathId}
          date={date}
          duration={duration}
          services={services}
          onTimeClick={onTimeClick}
          onRefresh={onRefresh}
        />
      )}
    </Space>
  );
};

export default React.memo(StylistOpenings);
